import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { activateSubscription } from '@/lib/api/core';
import { useMembershipPlans } from '@/hooks/useMembershipPlans';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Button, Card, CardTitle, Badge, toast } from '@/components/ui';
import { formatCOP } from '@/lib/format';
import { cycleLabel, formatExpiryDate } from '@/lib/membershipLifecycle';

type Cycle = 'monthly' | 'annual';
type PaymentMode = 'cash' | 'points' | 'mix';

/**
 * MembershipCheckout — ROUND12 / ROUND13 admin R27.
 *
 * Flujo de compra de suscripción con 3 modos de pago (alineado con Web R9):
 *  - Dinero (cash): 100% pasarela. RPC devuelve status=pending_payment +
 *    cash_remaining_cents → se simula la pasarela stub y se confirma.
 *  - Puntos (points): 100% saldo de puntos. Sólo habilitado si el user
 *    tiene `points >= pointsRequired`. RPC devuelve status=activated +
 *    ends_at (no pasa por la pasarela).
 *  - Mix: parte puntos + parte cash via slider. Sólo habilitado si el
 *    user tiene `points > 0`. RPC devuelve pending_payment con el
 *    `cash_remaining_cents` después de descontar `points_amount * 100`.
 *
 * Convención server-side admin R27: 1 punto = $100 COP (1 punto =
 * 10.000 centavos). El FE calcula `pointsRequired = priceCop / 100`.
 */

// 1 punto = 100 COP (admin R27 convención stable cross-surface).
const COP_PER_POINT = 100;

type ActivationResult = {
  ok: boolean;
  status: 'activated' | 'pending_payment';
  payment_mode: PaymentMode;
  points_used: number;
  cash_remaining_cents: number;
  currency: string;
  plan_id: string;
  cycle: Cycle;
  /** Sólo presente cuando status='activated'. */
  ends_at?: string;
  /** Otros campos que la RPC pueda devolver. */
  [key: string]: unknown;
};

type ActivatePayload = {
  planId: string;
  cycle: Cycle;
  paymentMode: PaymentMode;
  pointsAmount?: number;
};

export function MembershipCheckout({
  tierSlug,
  cycle,
}: {
  tierSlug: string;
  cycle: Cycle;
}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: plans, isLoading, isError } = useMembershipPlans();
  const { data: profile } = useUserProfile();

  const userPoints = profile?.points ?? 0;

  const [activation, setActivation] = useState<ActivationResult | null>(null);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('cash');
  const [mixPoints, setMixPoints] = useState<number>(0);
  // Stub gateway intermediate: cuando RPC devuelve pending_payment, mostramos
  // un step "pasarela en modo stub" que el usuario confirma.
  const [stubFinalized, setStubFinalized] = useState(false);

  const mutate = useMutation<ActivationResult, Error, ActivatePayload>({
    mutationFn: async ({ planId, cycle: c, paymentMode: pm, pointsAmount }) => {
      // GAP-04 (ver endpoint_gaps.md): `POST /v1/subscriptions/activate` sólo
      // acepta { plan_id, cycle } (modo dinero). Los modos puntos/mix no
      // tienen endpoint todavía; los tabs quedan deshabilitados en la UI.
      const raw = await activateSubscription(planId, c);
      // Adaptamos al shape que el render espera. El endpoint actual activa
      // de inmediato (cash); reportamos `activated`.
      const r = raw as Record<string, unknown>;
      return {
        ok: true,
        status: 'activated',
        payment_mode: pm,
        points_used: typeof pointsAmount === 'number' ? 0 : 0,
        cash_remaining_cents: 0,
        currency: 'COP',
        plan_id: planId,
        cycle: c,
        ends_at: typeof r.ends_at === 'string' ? r.ends_at : undefined,
        ...r,
      } as ActivationResult;
    },
    onSuccess: async (result) => {
      setActivation(result);
      await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      if (result.status === 'activated') {
        toast.success({
          title: '¡Membresía activada!',
          description: result.ends_at
            ? `Tu plan está activo hasta ${formatExpiryDate(result.ends_at)}.`
            : 'Tu plan quedó activo.',
        });
      } else {
        toast.info({
          title: 'Falta confirmar el pago',
          description: 'Te llevamos a la pasarela para terminar la compra.',
        });
      }
    },
    onError: (err) => {
      const msg = err.message || '';
      // Mapeo de errores conocidos del trigger v2.
      let description = msg || 'Probá de nuevo en unos segundos.';
      if (msg.includes('insufficient_points')) {
        description = 'No tenés suficientes puntos para cubrir este plan. Probá modo Mix o Dinero.';
      } else if (msg.includes('not_enough_balance')) {
        description = 'Tu saldo de puntos es menor a lo que querés aplicar. Ajustá el slider.';
      } else if (msg.includes('invalid_mix_zero_points')) {
        description = 'En modo Mix tenés que aplicar al menos 1 punto.';
      }
      toast.error({ title: 'No pudimos activar tu membresía', description });
    },
  });

  if (isLoading) {
    return (
      <section className="container-fc py-12" aria-busy="true">
        <div className="h-8 w-56 rounded bg-neutral-100" />
        <div className="mt-4 h-5 w-full max-w-md rounded bg-neutral-100" />
        <div className="mt-8 h-40 w-full rounded-card bg-neutral-100" />
      </section>
    );
  }

  const tier = plans?.find((p) => p.slug === tierSlug) ?? null;

  if (isError || !tier) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">Plan no encontrado</h1>
        <p className="mt-2 text-fc-muted">
          El plan que querés activar no existe o no está disponible.
        </p>
        <Button className="mt-6" variant="secondary" onClick={() => navigate('/memberships')}>
          Ver membresías
        </Button>
      </section>
    );
  }

  const isFree = tier.monthlyPriceCents === 0;
  // ROUND21 #3 — guardas defensivas. `monthlyPriceCents`/`annualPriceCents`
  // están normalizados a PESOS COP en el hook (`useMembershipPlans` divide
  // centavos / 100). Para First Class: priceCop = 150.000 → 1.500 pts.
  // Para Premium: priceCop = 50.000 → 500 pts. Si el backend retornara un
  // valor inesperado (0 con plan no-free, NaN, etc.) cae a 0 — el botón
  // queda deshabilitado por `canPayWithPoints = false`.
  const priceCop =
    cycle === 'annual' && typeof tier.annualPriceCents === 'number' && tier.annualPriceCents > 0
      ? tier.annualPriceCents
      : typeof tier.monthlyPriceCents === 'number' && tier.monthlyPriceCents >= 0
        ? tier.monthlyPriceCents
        : 0;
  const pointsRequired =
    Number.isFinite(priceCop) && priceCop > 0 ? Math.ceil(priceCop / COP_PER_POINT) : 0;
  // earnedPoints = puntos a GANAR por suscribirse (rebate), NO el costo.
  // Mostrado por separado en el UI como "+X pts".
  const earnedPoints = cycle === 'annual' ? tier.pointsAnnual : tier.pointsMonthly;
  // GAP-04 — el endpoint actual sólo soporta pago en dinero. Deshabilitamos
  // los modos puntos/mix hasta que el backend exponga `payment_mode`.
  const canPayWithPoints = false;
  const canMix = false;

  // Success state — render distinto según status:
  //   - activated → todo OK (puntos cubrieron + activación inmediata).
  //   - pending_payment → mostrar paso de pasarela stub para finalizar.
  if (activation) {
    return renderActivationResult(activation, tier, cycle, stubFinalized, () => {
      setStubFinalized(true);
      toast.success({
        title: 'Pago confirmado (stub)',
        description: 'En el gateway real este paso te redirige a la pasarela.',
      });
    });
  }

  // Sanity: si el mode actual no es válido para el user, caemos a cash.
  const effectiveMode: PaymentMode =
    paymentMode === 'points' && !canPayWithPoints
      ? 'cash'
      : paymentMode === 'mix' && !canMix
        ? 'cash'
        : paymentMode;

  const mixCashRemaining = Math.max(0, priceCop - mixPoints * COP_PER_POINT);
  const mixSliderMax = Math.min(userPoints, Math.max(0, pointsRequired - 1));

  function handleConfirm(): void {
    if (!tier) return; // narrowing safety en closure (TS18047)
    if (effectiveMode === 'mix' && (mixPoints <= 0 || mixPoints > userPoints)) {
      toast.error({
        title: 'Cantidad de puntos inválida',
        description: 'Elegí un valor entre 1 y tu saldo actual.',
      });
      return;
    }
    mutate.mutate({
      planId: tier.id,
      cycle,
      paymentMode: effectiveMode,
      // ROUND22 (P0 #1) — `points` mode también requiere `pointsAmount`
      // (= pointsRequired). El RPC v2 lo lee y debita esa cantidad.
      pointsAmount:
        effectiveMode === 'points'
          ? pointsRequired
          : effectiveMode === 'mix'
            ? mixPoints
            : undefined,
    });
  }

  const confirmLabel = mutate.isPending
    ? 'Procesando…'
    : isFree
      ? 'Activar plan gratuito'
      : effectiveMode === 'points'
        ? 'Pagar con puntos'
        : effectiveMode === 'mix'
          ? 'Continuar al pago'
          : 'Confirmar pago';

  return (
    <section className="container-fc py-8 md:py-12">
      <header className="mb-6">
        <h1 className="font-display text-3xl md:text-4xl">Suscribirme a la membresía</h1>
        <p className="mt-2 text-sm text-fc-muted">
          Revisá los datos de tu suscripción antes de confirmar el pago.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card variant="default" padded className="md:col-span-2">
          <CardTitle>Plan seleccionado</CardTitle>
          <dl className="mt-4 grid grid-cols-1 gap-y-3 text-sm md:grid-cols-3">
            <dt className="text-fc-muted">Plan</dt>
            <dd className="text-fc-ink md:col-span-2">
              <span className="font-medium">{tier.name}</span>
            </dd>
            <dt className="text-fc-muted">Ciclo</dt>
            <dd className="text-fc-ink md:col-span-2">{cycleLabel(cycle)}</dd>
            <dt className="text-fc-muted">Precio</dt>
            <dd className="tabular-nums text-fc-ink md:col-span-2">
              {isFree
                ? 'Gratis'
                : `${formatCOP(priceCop)} ${cycle === 'annual' ? '/ año' : '/ mes'}`}
            </dd>
            {earnedPoints > 0 && (
              <>
                <dt className="text-fc-muted">Puntos a ganar</dt>
                <dd className="text-fc-ink md:col-span-2">
                  +{earnedPoints.toLocaleString('es-CO')} pts
                </dd>
              </>
            )}
          </dl>

          {!isFree && (
            <PaymentModeSelector
              mode={effectiveMode}
              onChange={(m) => {
                setPaymentMode(m);
                if (m === 'mix' && mixPoints <= 0) {
                  // Slider arranca con el máximo razonable (todo el saldo o
                  // el máximo permitido por el plan).
                  setMixPoints(Math.max(1, Math.min(userPoints, pointsRequired - 1)));
                }
              }}
              canPayWithPoints={canPayWithPoints}
              canMix={canMix}
              userPoints={userPoints}
              pointsRequired={pointsRequired}
            />
          )}

          {effectiveMode === 'mix' && canMix && (
            <MixSlider
              value={mixPoints}
              max={mixSliderMax}
              onChange={setMixPoints}
              cashRemaining={mixCashRemaining}
            />
          )}
        </Card>

        <Card variant="outlined" padded>
          <div className="text-xs uppercase tracking-wide text-fc-muted">Total a pagar</div>
          <TotalDisplay
            mode={effectiveMode}
            priceCop={priceCop}
            pointsRequired={pointsRequired}
            mixPoints={mixPoints}
            mixCashRemaining={mixCashRemaining}
            isFree={isFree}
            cycle={cycle}
          />
          <Button
            className="mt-5 w-full justify-center"
            size="lg"
            onClick={handleConfirm}
            disabled={mutate.isPending}
          >
            {confirmLabel}
          </Button>
          <Button
            as={Link}
            to={`/memberships/${tier.slug}?cycle=${cycle}`}
            variant="ghost"
            size="sm"
            className="mt-2 w-full justify-center"
          >
            Volver al plan
          </Button>
          <p className="mt-4 text-xs text-fc-muted">
            En esta entrega usamos la pasarela en modo prueba (stub). El cobro real se integra con
            el gateway antes del lanzamiento público.
          </p>
        </Card>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function PaymentModeSelector({
  mode,
  onChange,
  canPayWithPoints,
  canMix,
  userPoints,
  pointsRequired,
}: {
  mode: PaymentMode;
  onChange: (m: PaymentMode) => void;
  canPayWithPoints: boolean;
  canMix: boolean;
  userPoints: number;
  pointsRequired: number;
}): JSX.Element {
  return (
    <div className="mt-6">
      <div className="text-xs uppercase tracking-wide text-fc-muted">Método de pago</div>
      <div role="tablist" aria-label="Método de pago" className="mt-2 grid grid-cols-3 gap-2">
        <PaymentModeTab
          active={mode === 'cash'}
          enabled={true}
          onClick={() => onChange('cash')}
          label="Dinero"
          hint="100% en pasarela"
        />
        <PaymentModeTab
          active={mode === 'points'}
          enabled={canPayWithPoints}
          onClick={() => onChange('points')}
          label="Puntos"
          hint={
            canPayWithPoints
              ? `${pointsRequired.toLocaleString('es-CO')} pts`
              : `Faltan ${(pointsRequired - userPoints).toLocaleString('es-CO')} pts`
          }
        />
        <PaymentModeTab
          active={mode === 'mix'}
          enabled={canMix}
          onClick={() => onChange('mix')}
          label="Mix"
          hint={canMix ? 'Combinar puntos + pasarela' : 'Necesitás puntos'}
        />
      </div>
      <p className="mt-2 text-xs text-fc-muted">
        Tu saldo:{' '}
        <span className="font-medium tabular-nums text-fc-ink">
          {userPoints.toLocaleString('es-CO')} pts
        </span>{' '}
        · Equivalencia: <span className="tabular-nums">1 pt = $100 COP</span>
      </p>
    </div>
  );
}

function PaymentModeTab({
  active,
  enabled,
  onClick,
  label,
  hint,
}: {
  active: boolean;
  enabled: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={!enabled}
      className={[
        'rounded-card border px-3 py-3 text-left text-sm transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        active
          ? 'border-primary bg-primary/10 text-fc-ink shadow-sm'
          : enabled
            ? 'border-neutral-200 bg-neutral-100 text-fc-ink hover:border-primary/60'
            : 'cursor-not-allowed border-neutral-200 bg-neutral-100/50 text-fc-muted opacity-60',
      ].join(' ')}
    >
      <div className="font-medium">{label}</div>
      <div className="mt-1 text-xs text-fc-muted">{hint}</div>
    </button>
  );
}

function MixSlider({
  value,
  max,
  onChange,
  cashRemaining,
}: {
  value: number;
  max: number;
  onChange: (n: number) => void;
  cashRemaining: number;
}): JSX.Element {
  return (
    <div className="mt-4 rounded-card border border-fc-line bg-fc-bg/40 p-4">
      <label htmlFor="mix-points" className="text-xs uppercase tracking-wide text-fc-muted">
        Puntos a aplicar
      </label>
      <div className="mt-2 flex items-center gap-3">
        <input
          id="mix-points"
          type="range"
          min={1}
          max={Math.max(1, max)}
          step={1}
          value={Math.min(value, Math.max(1, max))}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={max <= 0}
          className="flex-1 accent-primary"
        />
        <input
          type="number"
          min={1}
          max={Math.max(1, max)}
          value={value}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n)) onChange(Math.max(1, Math.min(max, Math.round(n))));
          }}
          className="w-20 rounded-card border border-fc-line bg-fc-paper px-2 py-1 text-right text-sm tabular-nums text-fc-ink focus:border-fc-gold focus:outline-none focus:ring-2 focus:ring-fc-gold/40"
          aria-label="Cantidad de puntos"
        />
      </div>
      <p className="mt-3 text-sm tabular-nums">
        <span className="font-semibold text-fc-ink">{value.toLocaleString('es-CO')} pts</span>
        <span className="text-fc-muted"> + </span>
        <span className="font-semibold text-fc-ink">{formatCOP(cashRemaining)}</span>
        <span className="text-fc-muted"> en pasarela</span>
      </p>
    </div>
  );
}

function TotalDisplay({
  mode,
  priceCop,
  pointsRequired,
  mixPoints,
  mixCashRemaining,
  isFree,
  cycle,
}: {
  mode: PaymentMode;
  priceCop: number;
  pointsRequired: number;
  mixPoints: number;
  mixCashRemaining: number;
  isFree: boolean;
  cycle: Cycle;
}): JSX.Element {
  if (isFree) {
    return (
      <>
        <div className="mt-1 text-3xl font-semibold tabular-nums">Gratis</div>
        <div className="text-xs text-fc-muted">activación sin cargo</div>
      </>
    );
  }
  if (mode === 'points') {
    return (
      <>
        <div className="mt-1 text-3xl font-semibold tabular-nums">
          {pointsRequired.toLocaleString('es-CO')} pts
        </div>
        <div className="text-xs text-fc-muted">100% con puntos</div>
      </>
    );
  }
  if (mode === 'mix') {
    return (
      <>
        <div className="mt-1 text-2xl font-semibold tabular-nums leading-tight">
          {mixPoints.toLocaleString('es-CO')} pts
        </div>
        <div className="tabular-nums text-fc-muted">+ {formatCOP(mixCashRemaining)}</div>
        <div className="mt-1 text-xs text-fc-muted">parte en puntos · resto en pasarela</div>
      </>
    );
  }
  // cash
  return (
    <>
      <div className="mt-1 text-3xl font-semibold tabular-nums">{formatCOP(priceCop)}</div>
      <div className="text-xs text-fc-muted">
        {cycle === 'annual' ? 'cobro único anual' : 'cobro mensual'}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Render del resultado de la RPC v2
// ---------------------------------------------------------------------------

function renderActivationResult(
  activation: ActivationResult,
  tier: { name: string },
  cycle: Cycle,
  stubFinalized: boolean,
  onSimulateGateway: () => void,
): JSX.Element {
  const isActivated = activation.status === 'activated' || stubFinalized;
  if (isActivated) {
    return (
      <section className="container-fc py-12">
        <Card variant="outlined" padded>
          <Badge tone="success">Activa</Badge>
          <CardTitle className="mt-3">
            Membresía {tier.name} {cycleLabel(cycle)} activa
            {activation.ends_at ? <> hasta {formatExpiryDate(activation.ends_at)}</> : null}
          </CardTitle>
          <p className="mt-2 text-sm text-fc-muted">
            Tu suscripción quedó registrada en tu cuenta. Ya podés usar todos los beneficios.
            {activation.points_used > 0 && (
              <>
                {' '}
                Aplicaste{' '}
                <strong className="text-fc-ink">
                  {activation.points_used.toLocaleString('es-CO')} pts
                </strong>{' '}
                al pago.
              </>
            )}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button as={Link} to="/account/membership" variant="primary">
              Ir a mi membresía
            </Button>
            <Button as={Link} to="/memberships" variant="ghost">
              Ver otros planes
            </Button>
          </div>
        </Card>
      </section>
    );
  }
  // pending_payment — stub gateway step.
  const cashCop = Math.round(activation.cash_remaining_cents / 100);
  return (
    <section className="container-fc py-12">
      <Card variant="outlined" padded>
        <Badge tone="warning">Pago pendiente</Badge>
        <CardTitle className="mt-3">Falta confirmar el pago en la pasarela</CardTitle>
        <p className="mt-2 text-sm text-fc-muted">
          Tu suscripción a <strong className="text-fc-ink">{tier.name}</strong> ({cycleLabel(cycle)}
          ) quedó pre-registrada. Faltan{' '}
          <strong className="tabular-nums text-fc-ink">{formatCOP(cashCop)}</strong>{' '}
          {activation.points_used > 0 && (
            <>
              después de aplicar{' '}
              <strong className="text-fc-ink">
                {activation.points_used.toLocaleString('es-CO')} pts
              </strong>{' '}
            </>
          )}
          que cobramos por la pasarela.
        </p>
        <div className="mt-4 rounded-card border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fc-ink">
          En modo stub no hay redirect real al gateway. Hacé clic en{' '}
          <strong>Simular pago aprobado</strong> para terminar el flujo y activar la suscripción.
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={onSimulateGateway}>Simular pago aprobado (stub)</Button>
          <Button as={Link} to="/account/membership" variant="ghost">
            Ver estado en mi cuenta
          </Button>
        </div>
      </Card>
    </section>
  );
}
